/**
 * CheckboxCell — Checkbox component for table row selection.
 *
 * Features:
 * - Supports header checkbox for "select all filtered"
 * - Individual row checkboxes for single selection
 * - Accessible with proper ARIA attributes
 * - Handles indeterminate state for partial selection
 */

import { Check, Minus } from '@untitledui/icons';
import React from 'react';

import { cx } from '@/utils/cx';

export interface CheckboxCellProps {
  /** Whether the checkbox is checked */
  isChecked: boolean;
  /** Whether the checkbox is in indeterminate state (for header "some selected") */
  isIndeterminate?: boolean;
  /** Change handler - receives checked state and the triggering event for shift-select support */
  onChange: (
    checked: boolean,
    event: React.MouseEvent | React.KeyboardEvent
  ) => void;
  /** Accessible label */
  'aria-label': string;
  /** Whether the checkbox is disabled */
  disabled?: boolean;
}

/**
 * Checkbox component for table selection.
 * Renders a custom checkbox using design system colors.
 */
export const CheckboxCell = ({
  isChecked,
  isIndeterminate = false,
  onChange,
  'aria-label': ariaLabel,
  disabled = false,
}: CheckboxCellProps) => {
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!disabled) {
      onChange(!isChecked, e);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled) {
        onChange(!isChecked, e);
      }
    }
  };

  return (
    <div
      role='checkbox'
      aria-checked={isIndeterminate ? 'mixed' : isChecked}
      aria-label={ariaLabel}
      aria-disabled={disabled || undefined}
      tabIndex={-1}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={cx(
        'flex items-center justify-center',
        'w-3.5 h-3.5 rounded transition-colors cursor-pointer',
        // Remove all focus styling since tabIndex=-1 (focus managed by parent cell)
        'outline-none',
        isChecked || isIndeterminate
          ? 'bg-[var(--color-brand-600)] text-white'
          : 'border border-[var(--color-border-secondary)] hover:border-[var(--color-border-primary)]',
        disabled && 'opacity-50 cursor-not-allowed'
      )}
    >
      {isIndeterminate && <Minus className='w-2 h-2' />}
      {!isIndeterminate && isChecked && <Check className='w-2 h-2' />}
    </div>
  );
};
